/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from "@hookform/resolvers/yup"
import { ActionButton, Button, NumberInput, Select } from "components"
import { Fragment, useEffect, useMemo } from "react"
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  UseFormReturn,
  useWatch
} from "react-hook-form"
import { TbEye } from "react-icons/tb"
import type { SelectOptionType } from "types"
import { useToggle } from "utils"
import {
  useProductAvailableList,
  useSohByProduct
} from "../../../utils/hooks"
import {
  formTransferDetailSchema,
  FormTransferDetailType,
  FormTransferItemsType
} from "../../../types"
import { DropdownProductAvailable } from "../../DropdownProductAvailable"

type Action = "add" | "edit"

type RowProps = {
  index?: number
  action: Action
  defaultValues?: any
  isHideForm?: boolean
  onCancelEditing?: () => void
  onDeleteJournal?: () => void
  onSubmit: (
    value: FormTransferDetailType,
    methods?: UseFormReturn<FormTransferDetailType>
  ) => void
  products: ReturnType<typeof useProductAvailableList>
  warehouseOptions: SelectOptionType[]
  isWarehouseLoading: boolean
}

export function RowTransferItem(props: RowProps) {
  const isHideForm = useToggle(props.isHideForm ?? true)

  const defaultValues: FormTransferDetailType = useMemo(
    () => ({
      transfer_detail_id: props?.defaultValues?.transfer_detail_id,
      product_id: props?.defaultValues?.product_id ?? NaN,
      product_name: props?.defaultValues?.product_name ?? "",
      product_barcode: props?.defaultValues?.product_barcode ?? "",
      quantity_transfer: props?.defaultValues?.quantity_transfer ?? 0,
      mr_unit_id: props?.defaultValues?.mr_unit_id ?? 0,
      mr_unit_name: props?.defaultValues?.mr_unit_name ?? "",
      warehouse_name: props?.defaultValues?.warehouse_name ?? "",
      location_warehouse_id: props?.defaultValues?.location_warehouse_id ?? NaN,
      soh: props?.defaultValues?.soh ?? 0,
      soh_destination: props?.defaultValues?.soh_destination ?? 0
    }),
    [props.defaultValues]
  )
  const methodsParent = useFormContext<FormTransferItemsType>()

  const location_from_id = useWatch({
    control: methodsParent.control,
    name: "location_from_id"
  })

  const location_to_id = useWatch({
    control: methodsParent.control,
    name: "location_to_id"
  })
  
  const methods = useForm<FormTransferDetailType>({
    defaultValues,
    resolver: yupResolver(formTransferDetailSchema)
  })

  const product_id = useWatch({
    control: methods.control,
    name: "product_id"
  })

  const sourceSoh = useSohByProduct({
    location_id: Number(location_from_id),
    product_id: Number(product_id)
  })

  const warehouseOptions = useMemo(() => {
    if (!product_id || Number.isNaN(Number(product_id))) {
      return []
    }

    if ((sourceSoh.data.soh ?? 0) <= 0) {
      return []
    }

    return props.warehouseOptions
  }, [product_id, sourceSoh.data.soh, props.warehouseOptions])

  const parentDetails = useWatch({
    control: methodsParent.control,
    name: "transfer_detail"
  })

  const isDuplicateProductWarehouse = useMemo(() => {
    const selectedProductId = Number(product_id)
    const selectedWarehouseId = Number(methods.watch("location_warehouse_id"))

    if (!selectedProductId || Number.isNaN(selectedProductId)) {
      return false
    }

    if (!selectedWarehouseId || Number.isNaN(selectedWarehouseId)) {
      return false
    }

    return (parentDetails ?? []).some((item, idx) => {
      if (typeof props.index === "number" && idx === props.index) {
        return false
      }

      return (
        Number(item.product_id) === selectedProductId &&
        Number(item.location_warehouse_id) === selectedWarehouseId
      )
    })
  }, [parentDetails, product_id, props.index, methods])

  const handleToggleHideForm = () => {
    if (!location_from_id && !location_to_id) {
      return methodsParent.trigger(["location_from_id", "location_to_id"]);
    }

    isHideForm.toggle()
  }


  useEffect(() => methods.reset(defaultValues), [defaultValues])
  useEffect(() => {
    methods.setValue("soh", sourceSoh.data.soh ?? 0)
  }, [sourceSoh.data.soh, methods])

  useEffect(() => {
    if (warehouseOptions.length === 0) {
      methods.setValue("location_warehouse_id", NaN)
      methods.setValue("warehouse_name", "")
      return
    }

    const selectedWarehouseId = methods.getValues("location_warehouse_id")
    const hasSelectedWarehouse = warehouseOptions.some(
      (item) => Number(item.value) === selectedWarehouseId
    )

    if (!hasSelectedWarehouse) {
      methods.setValue("location_warehouse_id", NaN)
      methods.setValue("warehouse_name", "")
    }
  }, [warehouseOptions, methods])

  return (
    <FormProvider {...methods}>
      <tr className="no-padding-body">
        <td className="text-center">
          {isHideForm.isActive && (
            <Button color="transparent" onClick={handleToggleHideForm}>
              <TbEye className="size-5" />
            </Button>
          )}
        </td>
        {isHideForm.isActive ? (
          <td colSpan={14} />
        ) : (
          <Fragment>
            <td className="w-[250px]">
              <DropdownProductAvailable 
                products={props.products} 
                location_to_id={location_to_id}
              />
            </td>

            <td>
              {
                <Controller
                  control={methods.control}
                  name="product_name"
                  render={({ field }) => (
                    <div className="w-[250px] px-3">{field.value}</div>
                  )}
                />
              }
            </td>

            <td className="w-[250px]">
              <Controller
                control={methods.control}
                name="location_warehouse_id"
                render={({ field, fieldState }) => (
                  <div>
                    <Select
                      className="w-full"
                      placeholder={
                        warehouseOptions.length === 0
                          ? "No warehouse"
                          : "Select warehouse"
                      }
                      isLoading={props.isWarehouseLoading}
                      isDisabled={
                        props.isWarehouseLoading ||
                        warehouseOptions.length === 0
                      }
                      options={warehouseOptions}
                      error={fieldState.error?.message}
                      value={
                        field.value
                          ? warehouseOptions.find(
                            (option) => Number(option.value) === field.value
                          ) ?? null
                          : null
                      }
                      onChange={(option: any) => {
                        const selectedId = option?.value ? Number(option.value) : NaN
                        field.onChange(selectedId)
                        methods.setValue("warehouse_name", option?.label ?? "")
                        sourceSoh.refetch()
                      }}
                    />

                    {isDuplicateProductWarehouse && (
                      <p className="text-error text-xs mt-1">
                        Duplicate item + warehouse on another row
                      </p>
                    )}
                  </div>
                )}
              />
            </td>

            <td className="text-right">
              {
                <Controller
                  control={methods.control}
                  name="soh"
                  render={({ field }) => (
                    <div className="px-3">{field.value}</div>
                  )}
                />
              }
            </td>

            <td className="text-right">
              {
                <Controller
                  control={methods.control}
                  name="soh_destination"
                  render={({ field }) => (
                    <div className="px-3">{field.value}</div>
                  )}
                />
              }
            </td>

            <td>
              {
                <Controller
                  control={methods.control}
                  name="mr_unit_name"
                  render={({ field }) => (
                    <div className="px-3">{field.value}</div>
                  )}
                />
              }
            </td>

            <td className="text-right">
              <Controller
                control={methods.control}
                name="quantity_transfer"
                render={({ field, fieldState }) => (
                  <NumberInput
                    textRight
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    value={field.value}
                    ref={field.ref}
                    onValueChange={(value) => {
                      field.onChange(value.floatValue)
                    }}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </td>

            <td>
              <div className="text-center flex justify-center items-center">
                {props.action === "edit" && (
                  <>
                    <ActionButton.Check
                      onClick={() => {
                        methods.handleSubmit((data) => props.onSubmit(data, methods))()
                      }}
                    />
                    <ActionButton.Close onClick={props?.onCancelEditing} />
                  </>
                )}

                {props.action === "add" && (
                  <>
                    <ActionButton.Add
                      onClick={() => {
                        methods.handleSubmit((data) =>
                          props.onSubmit(data, methods)
                        )()
                      }}
                    />
                    <ActionButton.Close
                      onClick={() => {
                        isHideForm.toggle()
                        methods.reset(defaultValues)
                      }}
                    />
                  </>
                )}
              </div>
            </td>
          </Fragment>
        )}
      </tr>
    </FormProvider>
  )
}
